import { EndpointUrl } from 'api/endpoint';
import { useWorkspaceFiles } from 'api/files';
import axios from 'axios';
import { useNotifications } from 'components/notify';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB in bytes
const CONCURRENT_UPLOADS = 5;

interface UploadProgress {
  progress: number;
  status: string;
  uploading: boolean;
}

interface FileUploadHook {
  uploadFile: (
    file: File,
  ) => Promise<{ fileName: string; uploadId: string; workspaceId: string }>;
  uploadProgress: UploadProgress;
  resetProgress: () => void;
}

export const useFileUpload = (): FileUploadHook => {
  const { workspaceId } = useParams();
  const { mutate: refreshFiles } = useWorkspaceFiles();
  const { showNotification } = useNotifications();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');

  const uploadFile = useCallback(
    async (file: File) => {
      if (!workspaceId) return;

      setUploading(true);
      setUploadProgress(0);
      setUploadStatus('Creating upload...');

      try {
        const token = localStorage.getItem('token');
        const headers = {
          'x-finsight-token': token || '',
        };

        // Step 1: Create file record
        const createResponse = await axios.post(
          `${EndpointUrl}/workspace/${workspaceId}/files/create`,
          {
            fileName: file.name,
            fileSize: file.size,
          },
          { headers },
        );

        const { uploadId } = createResponse.data;

        // Check if this is a multipart upload (file > 50MB)
        if (file.size > CHUNK_SIZE) {
          setUploadStatus('Starting multipart upload...');

          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
          let completedParts = 0;
          const partProgress = new Array(totalChunks).fill(0);

          // Upload parts concurrently with limit
          for (let i = 0; i < totalChunks; i += CONCURRENT_UPLOADS) {
            const batch = [];

            // Create batch of upload promises
            for (
              let j = 0;
              j < CONCURRENT_UPLOADS && i + j < totalChunks;
              j++
            ) {
              const partIndex = i + j;
              const start = partIndex * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const partNumber = partIndex + 1;

              const chunk = file.slice(start, end);
              const formData = new FormData();
              formData.append('file', chunk);

              const uploadPromise = axios.post(
                `${EndpointUrl}/workspace/${workspaceId}/files/${uploadId}/upload?partNumber=${partNumber}`,
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                    'x-finsight-token': token || '',
                  },
                  onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                      // Calculate progress for this specific part (0-100%)
                      const partProgressValue =
                        progressEvent.loaded / progressEvent.total;
                      partProgress[partIndex] = partProgressValue;

                      // Calculate overall progress based on all parts
                      const totalProgress = partProgress.reduce(
                        (sum, progress) => sum + progress,
                        0,
                      );
                      const overallProgress =
                        (totalProgress / totalChunks) * 100;
                      setUploadProgress(Math.round(overallProgress));
                    }
                  },
                },
              );

              batch.push(uploadPromise);
            }

            // Upload batch concurrently
            setUploadStatus(
              `Uploading parts ${i + 1}-${Math.min(
                i + CONCURRENT_UPLOADS,
                totalChunks,
              )}/${totalChunks}...`,
            );

            await Promise.all(batch);
            completedParts += batch.length;

            // Update progress after batch completion
            const batchProgress = (completedParts / totalChunks) * 100;
            setUploadProgress(Math.round(batchProgress));
          }

          setUploadStatus('Upload completed!');
          setUploadProgress(100);
        } else {
          // Single file upload (≤ 50MB)
          setUploadStatus('Uploading file...');

          const formData = new FormData();
          formData.append('file', file);

          await axios.post(
            `${EndpointUrl}/workspace/${workspaceId}/files/${uploadId}/upload`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                'x-finsight-token': token || '',
              },
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const progress = Math.round(
                    (progressEvent.loaded / progressEvent.total) * 100,
                  );
                  setUploadProgress(progress);
                }
              },
            },
          );

          setUploadStatus('Upload completed!');
          setUploadProgress(100);
        }

        showNotification(`File "${file.name}" uploaded successfully!`, {
          variant: 'success',
        });

        // Refresh files list
        refreshFiles();

        return createResponse.data;
      } catch (error) {
        let errorMessage = 'Upload failed';

        if (axios.isAxiosError(error)) {
          if (error.response) {
            // Server responded with error status
            errorMessage = `Upload failed: ${error.response.status} ${error.response.statusText}`;
          } else if (error.request) {
            // Network error
            errorMessage = 'Network error occurred';
          } else {
            // Other error
            errorMessage = error.message;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        showNotification(errorMessage, {
          variant: 'error',
        });
        console.error('Upload failed:', error);
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }
    },
    [workspaceId, showNotification, refreshFiles],
  );

  const resetProgress = useCallback(() => {
    setUploading(false);
    setUploadProgress(0);
    setUploadStatus('');
  }, []);

  return {
    uploadFile,
    uploadProgress: {
      progress: uploadProgress,
      status: uploadStatus,
      uploading,
    },
    resetProgress,
  };
};
